import { ConditionalRender } from '@ui';
import { useMemo } from 'react';
import { clinicalManagementGroups } from '@/constants/user.constants';
import { ResourceSection } from '../ResourceSection';
import { NoContacts } from '../NoContacts';
import { AssignedUsersByGroup, CaseUserGroup, EmployeeLabels } from '@/api/apis/case';
import { EmployeeCard } from '../EmployeeCard';
import { Uuid } from '@/api/types';
import { sortUsersByGroup } from '@/utils/helpers';
import { CLINICAL_CASE_MANAGEMENT_GROUP_WEIGHT } from '@/constants/case/case.constants';
import { PrimaryWarningBanner } from '../PrimaryWarningBanner';

type CaseManagementProps = {
  assignedEmployees: AssignedUsersByGroup;
  menuDisabled: boolean;
  caseId: Uuid;
};

export const CaseManagement = ({ assignedEmployees, menuDisabled, caseId }: CaseManagementProps) => {
  const users = useMemo(() => {
    const list = clinicalManagementGroups.flatMap((group) => {
      return assignedEmployees[group as CaseUserGroup] ?? [];
    });

    return sortUsersByGroup(list, CLINICAL_CASE_MANAGEMENT_GROUP_WEIGHT, true);
  }, [assignedEmployees]);

  const primaryAvailableUsers = useMemo(() => {
    let availableUsers = {};
    for (const [key, value] of Object.entries(assignedEmployees)) {
      const currentGroup = assignedEmployees[key as CaseUserGroup];
      if (currentGroup && clinicalManagementGroups.includes(key as CaseUserGroup) && currentGroup.length > 0) {
        availableUsers = {
          ...availableUsers,
          [key]: value,
        };
      }
    }

    return availableUsers;
  }, [assignedEmployees]);

  return (
    <ResourceSection title="Clinical Case Management" count={users.length}>
      <PrimaryWarningBanner userGroups={clinicalManagementGroups} assignedEmployees={assignedEmployees} />
      <ConditionalRender show={!!users.length} placeholder={<NoContacts />}>
        {users.map(({ user, userId, employeeId, group, isPrimary }) => (
          <EmployeeCard
            menuDisabled={menuDisabled}
            key={`${userId || employeeId}_${group}`}
            employeeId={employeeId!}
            user={user}
            group={group}
            primaryAvailableUsers={primaryAvailableUsers}
            labels={isPrimary ? [EmployeeLabels.Primary] : []}
            caseId={caseId}
            assignedUsersInGroup={assignedEmployees[group as CaseUserGroup] || []}
          />
        ))}
      </ConditionalRender>
    </ResourceSection>
  );
};
